// Client-side canvas compressor. Keeps compressing (downscaling + lowering
// JPEG quality) until the resulting Blob is <= targetBytes. Falls back to the
// smallest result achieved if the target can't be met after all attempts.
//
// Used for the Upcoming Events picture uploader (100 KB cap on backend).

const readAsImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });

const canvasToBlob = (canvas, type, q) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, q));

export async function compressImageTo100KB(file, {
  targetBytes = 95 * 1024,       // aim just below 100 KB so any envelope overhead is safe
  maxDim = 1400,                 // starting max width/height (px)
  minDim = 480,                  // never shrink below this
  minQuality = 0.35,
} = {}) {
  if (!file || !file.type?.startsWith("image/")) throw new Error("Not an image");

  // Small enough already? Skip re-encode.
  if (file.size <= targetBytes) return file;

  const img = await readAsImage(file);
  let dim = maxDim;
  let quality = 0.85;
  let bestBlob = null;

  // Downscale + reduce quality progressively.
  for (let attempt = 0; attempt < 8; attempt++) {
    const scale = Math.min(1, dim / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    // Always encode as JPEG for best size (except very tiny PNGs already fine).
    const blob = await canvasToBlob(canvas, "image/jpeg", quality);
    if (blob && (!bestBlob || blob.size < bestBlob.size)) bestBlob = blob;
    if (blob && blob.size <= targetBytes) {
      return new File([blob], (file.name || "event").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
    }
    // Reduce more aggressively as we go.
    if (quality > minQuality) quality = Math.max(minQuality, quality - 0.12);
    else dim = Math.max(minDim, Math.round(dim * 0.82));
  }

  if (!bestBlob) throw new Error("Compression failed");
  return new File([bestBlob], (file.name || "event").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
}
